import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ArrowsRightLeftIcon,
  ArrowsPointingInIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ArrowUturnLeftIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import {
  useManagerTasks,
  useBulkMoveTasks,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useBulkDeleteTasks,
  useMergeTasks,
} from '../../hooks/useTasks';
import { MoveTasksModal } from '../../components/domain/MoveTasksModal';
import { MergeTasksModal } from '../../components/domain/MergeTasksModal';
import { TaskModal } from '../../components/domain/TaskModal';
import { TaskStateChip } from '../../components/domain/TaskStateChip';
import { useProjectStates } from '../../hooks/useProjectStates';
import { useProjects, useProject } from '../../hooks/useProjects';
import { useAuth } from '../../services/authContext';
import { chipTextColor } from '../../utils/format';
import type { ProjectWithTasks, TaskWithStats, BulkDeleteResult } from '../../services/api/tasks';
import type { TaskState } from '../../services/api/projectStates';

function formatHours(hours: number): string {
  return hours.toFixed(1);
}

type SortKey = 'name' | 'code' | 'origin' | 'entryCount' | 'totalHours' | 'plannedHours' | 'budget';
type SortDir = 'asc' | 'desc';

function sortTasks(tasks: TaskWithStats[], key: SortKey, dir: SortDir): TaskWithStats[] {
  return [...tasks].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'name':
        cmp = a.name.localeCompare(b.name);
        break;
      case 'code':
        cmp = (a.code ?? '').localeCompare(b.code ?? '');
        break;
      case 'origin':
        cmp = (a.origin ?? '').localeCompare(b.origin ?? '');
        break;
      case 'entryCount':
        cmp = a.entryCount - b.entryCount;
        break;
      case 'totalHours':
        cmp = a.totalHours - b.totalHours;
        break;
      case 'plannedHours':
        cmp = (a.plannedHours ?? -1) - (b.plannedHours ?? -1);
        break;
      case 'budget':
        cmp = (a.budget ?? -1) - (b.budget ?? -1);
        break;
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

function SortHeader({
  label,
  sortKey,
  currentKey,
  currentDir,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | null;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = currentKey === sortKey;
  const Icon = active ? (currentDir === 'asc' ? BarsArrowUpIcon : BarsArrowDownIcon) : ChevronUpDownIcon;
  return (
    <th
      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${align === 'right' ? 'text-right' : 'text-left'} ${active ? 'text-gray-900' : 'text-gray-500'}`}
      onClick={() => onSort(sortKey)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? 'text-gray-700' : 'text-gray-400'}`} />
      </span>
    </th>
  );
}

export function TaskManagementPage() {
  const { user: currentUser, isAdmin } = useAuth();

  // Admin-only "Show all projects" toggle. Non-admins can never trigger the
  // all-projects fetch from the UI (defense in depth; the server also re-checks).
  const [showAllProjects, setShowAllProjects] = useState(false);

  const { data: projects = [], isLoading, isFetching, error, refetch } = useManagerTasks(
    isAdmin && showAllProjects ? 'all' : undefined
  );
  const moveMutation = useBulkMoveTasks();
  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();
  const deleteMutation = useDeleteTask();
  const bulkDeleteMutation = useBulkDeleteTasks();
  const mergeMutation = useMergeTasks();

  const { data: managedProjectsList } = useProjects();

  // D-22 lookup: projectId → projectManagerId (or null when no PM assigned).
  // Phase 18 froze the /manager/tasks response (CONTEXT.md D-10), so we read
  // PM ids from the existing /api/projects list endpoint instead. Empty
  // (loading) → no rows are editable until ids resolve.
  const projectManagerById = useMemo(() => {
    const map = new Map<number, number | null>();
    for (const p of managedProjectsList ?? []) {
      map.set(p.id, p.projectManager?.id ?? null);
    }
    return map;
  }, [managedProjectsList]);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [originFilter, setOriginFilter] = useState<string>('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Per-project state filter (D-08, D-12). Outer key = projectId, inner Set
  // = included taskStateIds. Empty Set = "show all" within that project.
  // Resets on page reload (D-12 — no sessionStorage, no URL params).
  const [stateFilterByProject, setStateFilterByProject] = useState<Map<number, Set<number>>>(
    new Map()
  );

  const toggleStateFilter = (projectId: number, stateId: number) => {
    setStateFilterByProject((prev) => {
      const next = new Map(prev);
      const existing = new Set(next.get(projectId) ?? []);
      if (existing.has(stateId)) existing.delete(stateId);
      else existing.add(stateId);
      next.set(projectId, existing);
      return next;
    });
  };

  const clearStateFilter = (projectId: number) => {
    setStateFilterByProject((prev) => {
      const next = new Map(prev);
      next.delete(projectId);
      return next;
    });
  };

  const [searchParams, setSearchParams] = useSearchParams();
  // WR-02: track the *last consumed* deep-link key (projectId:stateId) instead
  // of a single boolean. Without this, a user who lands via deep-link, manually
  // clears the filter, then opens another conflict link in a follow-up modal
  // pointing at a different state would have the second deep-link silently
  // ignored (ref still true from the first consumption).
  const lastDeepLinkRef = useRef<string | null>(null);

  // Phase 20 D-19 / D-20: pre-select a per-project state filter chip when the
  // URL carries `?projectId=X&stateId=Y`. The deep-link is emitted by:
  //   - ReapplyTemplateModal 409 conflict link (plan 20-06).
  //   - ProjectFormPage delete-409 inline banner link (this plan, Task 1).
  // After consumption we strip the params with `replace: true` so refresh
  // doesn't re-trigger the seed. D-12's "in-memory only" constraint stays
  // intact — the URL is just the entry-point hint.
  useEffect(() => {
    const projectIdParam = searchParams.get('projectId');
    const stateIdParam = searchParams.get('stateId');
    if (!projectIdParam || !stateIdParam) return;
    const pid = Number(projectIdParam);
    const sid = Number(stateIdParam);
    if (!Number.isFinite(pid) || !Number.isFinite(sid)) return;

    const key = `${pid}:${sid}`;
    if (lastDeepLinkRef.current === key) return;
    lastDeepLinkRef.current = key;
    setStateFilterByProject((prev) => {
      const next = new Map(prev);
      next.set(pid, new Set([sid]));
      return next;
    });
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      next.add(pid);
      return next;
    });
    // Strip the query params so refresh doesn't re-apply the seed.
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [searchParams, setSearchParams]);

  // Task modal state
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithStats | undefined>();
  const [taskModalProjectId, setTaskModalProjectId] = useState<number>(0);
  const [taskModalError, setTaskModalError] = useState<string | null>(null);

  // Phase 49 Plan 49-05 — RC-01: fetch full project detail to read taskTypeId.
  // useProjects() list only returns ProjectListItem (no taskTypeId); useProject()
  // returns full Project which includes taskTypeId.
  const { data: taskModalProject } = useProject(taskModalProjectId || undefined);
  const taskModalProjectTaskTypeId = taskModalProject?.taskTypeId ?? null;

  // Delete confirm state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState<TaskWithStats | null>(null);

  // Merge modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergingTasks, setMergingTasks] = useState<TaskWithStats[]>([]);

  // Bulk delete confirm state
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState<BulkDeleteResult | null>(null);

  // F3-008: surface unarchive failures (403 on role change, 409 if peer
  // already unarchived, 500). Previously the fire-and-forget
  // `mutateAsync` rejection bubbled up as an unhandled promise.
  const [actionError, setActionError] = useState<string | null>(null);

  // Build a map of taskId -> projectId for quick lookup
  const taskProjectMap = useMemo(() => {
    const map = new Map<number, number>();
    for (const project of projects) {
      for (const task of project.tasks) {
        map.set(task.id, project.id);
      }
    }
    return map;
  }, [projects]);

  // Filter tasks based on search and origin
  const filteredProjects = useMemo(() => {
    return projects
      .map((project) => {
        const filteredTasks = project.tasks.filter((task) => {
          const matchesSearch =
            !searchQuery ||
            task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (task.code && task.code.toLowerCase().includes(searchQuery.toLowerCase()));

          const matchesOrigin =
            !originFilter ||
            (originFilter === '_none_' ? !task.origin : task.origin === originFilter);

          const isHidden = task.status === 'Deleted' || task.status === 'archived';
          const matchesDeleted = showDeleted || !isHidden;

          return matchesSearch && matchesOrigin && matchesDeleted;
        });

        return { ...project, tasks: filteredTasks };
      })
      .filter((project) => project.tasks.length > 0);
  }, [projects, searchQuery, originFilter, showDeleted]);

  // Get unique origins for filter dropdown
  const availableOrigins = useMemo(() => {
    const origins = new Set<string>();
    for (const project of projects) {
      for (const task of project.tasks) {
        if (task.origin) {
          origins.add(task.origin);
        }
      }
    }
    return Array.from(origins).sort();
  }, [projects]);

  // Toggle project expansion
  const toggleProject = (projectId: number) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  // Toggle task selection
  const toggleTask = (taskId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // Toggle all tasks in a project
  const toggleAllInProject = (project: ProjectWithTasks) => {
    const projectTaskIds = project.tasks.map((t) => t.id);
    const allSelected = projectTaskIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of projectTaskIds) {
          next.delete(id);
        }
      } else {
        for (const id of projectTaskIds) {
          next.add(id);
        }
      }
      return next;
    });
  };

  // Check if all tasks in a project are selected
  const isAllSelectedInProject = (project: ProjectWithTasks) => {
    return project.tasks.length > 0 && project.tasks.every((t) => selectedIds.has(t.id));
  };

  // Check if some but not all tasks are selected
  const isSomeSelectedInProject = (project: ProjectWithTasks) => {
    const hasSelected = project.tasks.some((t) => selectedIds.has(t.id));
    const hasUnselected = project.tasks.some((t) => !selectedIds.has(t.id));
    return hasSelected && hasUnselected;
  };

  // Handle move completion
  const handleMoveComplete = () => {
    setShowMoveModal(false);
    setSelectedIds(new Set());
  };

  // Get selected tasks info
  const selectedTasks = useMemo(() => {
    const tasks: TaskWithStats[] = [];
    for (const project of projects) {
      for (const task of project.tasks) {
        if (selectedIds.has(task.id)) {
          tasks.push(task);
        }
      }
    }
    return tasks;
  }, [projects, selectedIds]);

  // Get projects that can be targets (exclude projects that contain all selected tasks)
  const availableTargetProjects = useMemo(() => {
    if (selectedIds.size === 0) return [];

    const sourceProjectIds = new Set<number>();
    for (const taskId of selectedIds) {
      const projectId = taskProjectMap.get(taskId);
      if (projectId) {
        sourceProjectIds.add(projectId);
      }
    }

    const activeStates = ['in_progress', 'paused', 'draft'];
    return projects.filter((p) => {
      if (!p.state || !activeStates.includes(p.state)) return false;
      const selectedInThisProject = p.tasks.filter((t) => selectedIds.has(t.id)).length;
      return selectedInThisProject < selectedIds.size;
    });
  }, [projects, selectedIds, taskProjectMap]);

  // Check if all selected tasks can be bulk-merged (all editable + same project)
  const canBulkMerge = useMemo(() => {
    if (selectedTasks.length < 2) return false;
    const allEditable = selectedTasks.every((t) => t.origin === null || t.origin === 'MS');
    if (!allEditable) return false;
    const projectId = selectedTasks[0]!.projectId;
    return selectedTasks.every((t) => t.projectId === projectId);
  }, [selectedTasks]);

  // Clear filters
  const handleReset = () => {
    setSearchQuery('');
    setOriginFilter('');
    setShowDeleted(false);
    setSelectedIds(new Set());
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // --- Task CRUD handlers ---

  const handleUnarchive = async (task: TaskWithStats) => {
    // F3-008: wrap the fire-and-forget mutateAsync so a 4xx/5xx surfaces
    // inline instead of bubbling as an unhandled promise rejection.
    try {
      await updateMutation.mutateAsync({ id: task.id, data: { status: '' } });
      setActionError(null);
    } catch (err) {
      setActionError(
        `Unarchive failed for "${task.name}": ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  };

  const openCreateModal = (projectId: number) => {
    setEditingTask(undefined);
    setTaskModalProjectId(projectId);
    setTaskModalError(null);
    setTaskModalOpen(true);
  };

  const openEditModal = (task: TaskWithStats) => {
    setEditingTask(task);
    setTaskModalProjectId(task.projectId);
    setTaskModalError(null);
    setTaskModalOpen(true);
  };

  const handleTaskSave = async (data: {
    name: string;
    code?: string;
    status?: string;
    budget?: number | null;
    hardLimit?: boolean;
    plannedHours?: number | null;
    tierId?: number | null; // Phase 49 Plan 49-05 — RC-01: forwarded on create only
  }) => {
    setTaskModalError(null);
    try {
      if (editingTask) {
        await updateMutation.mutateAsync({
          id: editingTask.id,
          data: {
            name: data.name,
            status: data.status,
            budget: data.budget,
            hardLimit: data.hardLimit,
            plannedHours: data.plannedHours,
            // tierId is NOT forwarded to update — tier changes go through patchTaskTier
          },
        });
      } else {
        await createMutation.mutateAsync({
          projectId: taskModalProjectId,
          ...data,
          // Phase 49 Plan 49-05 — forward tierId only when explicitly set (SLA create)
          ...(data.tierId !== undefined ? { tierId: data.tierId } : {}),
        });
      }
      setTaskModalOpen(false);
    } catch (err) {
      setTaskModalError((err as Error).message);
    }
  };

  const openDeleteConfirm = (task: TaskWithStats) => {
    setDeletingTask(task);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTask) return;
    try {
      await deleteMutation.mutateAsync(deletingTask.id);
      setDeleteConfirmOpen(false);
      setDeletingTask(null);
      // Remove from selection if selected
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deletingTask.id);
        return next;
      });
    } catch {
      // error displayed in modal
    }
  };

  const openMergeModal = (task: TaskWithStats) => {
    setMergingTasks([task]);
    setMergeModalOpen(true);
  };

  const openBulkMergeModal = () => {
    setMergingTasks(selectedTasks);
    setMergeModalOpen(true);
  };

  const handleMergeComplete = () => {
    setMergeModalOpen(false);
    setMergingTasks([]);
    setSelectedIds(new Set());
  };

  // Get tasks from the same project and parent project as the merge sources (for target selection)
  const mergeProjectTasks = useMemo(() => {
    if (mergingTasks.length === 0) return [];
    const projectId = mergingTasks[0]!.projectId;
    const project = projects.find((p) => p.id === projectId);
    if (!project) return [];
    const tasks = [...project.tasks];
    if (project.parentId) {
      const parent = projects.find((p) => p.id === project.parentId);
      if (parent) {
        const existingIds = new Set(tasks.map((t) => t.id));
        for (const t of parent.tasks) {
          if (!existingIds.has(t.id)) tasks.push(t);
        }
      }
    }
    return tasks;
  }, [projects, mergingTasks]);

  const openBulkDeleteConfirm = () => {
    setBulkDeleteResult(null);
    setBulkDeleteConfirmOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const result = await bulkDeleteMutation.mutateAsync(Array.from(selectedIds));
      setBulkDeleteResult(result);
      setSelectedIds(new Set());
    } catch {
      // error displayed in modal
    }
  };

  const deleteConfirmMessage = deletingTask
    ? deletingTask.entryCount > 0
      ? `Task "${deletingTask.name}" has ${formatHours(deletingTask.totalHours)} hours logged. It will be archived instead of deleted.`
      : `Permanently delete task "${deletingTask.name}"?`
    : '';

  const showFullLoading = isLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Task Management</h1>
          {isFetching && !isLoading && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              <span>Updating...</span>
            </div>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500">View and manage tasks across your projects</p>
      </div>

      <DismissibleErrorBanner
        message={actionError}
        onDismiss={() => setActionError(null)}
        role="alert"
      />

      {/* Filters & Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or code..."
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">Origin</label>
            <select
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
            >
              <option value="">All origins</option>
              <option value="_none_">No origin</option>
              {availableOrigins.map((origin) => (
                <option key={origin} value={origin}>
                  {origin}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Show deleted
          </label>

          {isAdmin && (
            <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllProjects}
                onChange={(e) => setShowAllProjects(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show all projects
            </label>
          )}

          <Button variant="secondary" onClick={handleReset}>
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Reset
          </Button>

          {selectedIds.size > 0 && (
            <>
              <Button onClick={() => setShowMoveModal(true)}>
                <ArrowsRightLeftIcon className="h-4 w-4 mr-1" />
                Move {selectedIds.size} task{selectedIds.size !== 1 ? 's' : ''}
              </Button>
              {canBulkMerge && (
                <Button onClick={openBulkMergeModal}>
                  <ArrowsPointingInIcon className="h-4 w-4 mr-1" />
                  Merge {selectedIds.size} tasks
                </Button>
              )}
              <Button variant="danger" onClick={openBulkDeleteConfirm}>
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete {selectedIds.size} task{selectedIds.size !== 1 ? 's' : ''}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Results */}
      {showFullLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load tasks. Please try again.</p>
          <Button variant="secondary" onClick={() => refetch()} className="mt-2">
            Retry
          </Button>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">
            {projects.length === 0
              ? 'No tasks found in your managed projects.'
              : 'No tasks match your search criteria.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Project Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <button
                  onClick={() => toggleProject(project.id)}
                  className="flex items-center gap-3"
                >
                  {expandedProjects.has(project.id) ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                  )}
                  <h3 className="font-semibold text-gray-900">{project.fullName || project.name}</h3>
                  <span className="text-sm text-gray-500">
                    ({project.tasks.length} task{project.tasks.length !== 1 ? 's' : ''})
                  </span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openCreateModal(project.id);
                  }}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  title="New task"
                >
                  <PlusIcon className="h-4 w-4" />
                  New Task
                </button>
              </div>

              {/* Project body — per-project sub-component owns state fetch + filter chip-row + table */}
              {expandedProjects.has(project.id) && (
                <ProjectTasksTable
                  project={project}
                  currentUserId={currentUser?.id ?? null}
                  isAdmin={isAdmin}
                  projectManagerById={projectManagerById}
                  stateFilter={stateFilterByProject.get(project.id) ?? new Set<number>()}
                  onToggleState={(stateId) => toggleStateFilter(project.id, stateId)}
                  onClearFilter={() => clearStateFilter(project.id)}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  handleSort={handleSort}
                  isAllSelectedInProject={isAllSelectedInProject}
                  isSomeSelectedInProject={isSomeSelectedInProject}
                  toggleAllInProject={toggleAllInProject}
                  selectedIds={selectedIds}
                  toggleTask={toggleTask}
                  handleUnarchive={handleUnarchive}
                  openMergeModal={openMergeModal}
                  openEditModal={openEditModal}
                  openDeleteConfirm={openDeleteConfirm}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Move Tasks Modal */}
      <MoveTasksModal
        isOpen={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        onComplete={handleMoveComplete}
        selectedTasks={selectedTasks}
        targetProjects={availableTargetProjects}
        moveMutation={moveMutation}
      />

      {/* Merge Tasks Modal */}
      <MergeTasksModal
        isOpen={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        onComplete={handleMergeComplete}
        sourceTasks={mergingTasks}
        projectTasks={mergeProjectTasks}
        mergeMutation={mergeMutation}
      />

      {/* Create/Edit Task Modal */}
      <TaskModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSave={handleTaskSave}
        task={editingTask}
        projectId={taskModalProjectId}
        projectTaskTypeId={taskModalProjectTaskTypeId}
        isLoading={createMutation.isPending || updateMutation.isPending}
        error={taskModalError}
      />

      {/* Delete Single Task Confirm */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeletingTask(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Task"
        message={deleteConfirmMessage}
        confirmText={deletingTask && deletingTask.entryCount > 0 ? 'Archive' : 'Delete'}
        variant="danger"
        isLoading={deleteMutation.isPending}
        error={deleteMutation.isError ? (deleteMutation.error as Error).message : null}
      />

      {/* Bulk Delete Confirm */}
      {bulkDeleteResult ? (
        <ConfirmModal
          isOpen={bulkDeleteConfirmOpen}
          onClose={() => {
            setBulkDeleteConfirmOpen(false);
            setBulkDeleteResult(null);
          }}
          onConfirm={() => {
            setBulkDeleteConfirmOpen(false);
            setBulkDeleteResult(null);
          }}
          title="Bulk Delete Complete"
          message={
            [
              bulkDeleteResult.deleted.length > 0
                ? `${bulkDeleteResult.deleted.length} deleted`
                : null,
              bulkDeleteResult.archived.length > 0
                ? `${bulkDeleteResult.archived.length} archived`
                : null,
              bulkDeleteResult.failed.length > 0
                ? `${bulkDeleteResult.failed.length} failed`
                : null,
            ]
              .filter(Boolean)
              .join(', ') + '.'
          }
          confirmText="Done"
          variant="primary"
        />
      ) : (
        <ConfirmModal
          isOpen={bulkDeleteConfirmOpen}
          onClose={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={handleBulkDeleteConfirm}
          title="Delete Tasks"
          message={`Delete ${selectedIds.size} selected task${selectedIds.size !== 1 ? 's' : ''}? Tasks with logged hours will be archived instead.`}
          confirmText="Delete"
          variant="danger"
          isLoading={bulkDeleteMutation.isPending}
          error={bulkDeleteMutation.isError ? (bulkDeleteMutation.error as Error).message : null}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectTasksTable sub-component
//
// Declared at module scope (NOT nested inside TaskManagementPage) so that
// React's rules of hooks are satisfied — each project section calls
// useProjectStates(project.id) independently, one hook call per mount.
// ---------------------------------------------------------------------------

interface ProjectTasksTableProps {
  project: ProjectWithTasks;
  currentUserId: number | null;
  isAdmin: boolean;
  projectManagerById: Map<number, number | null>;
  stateFilter: Set<number>;
  onToggleState: (stateId: number) => void;
  onClearFilter: () => void;
  sortKey: SortKey | null;
  sortDir: SortDir;
  handleSort: (key: SortKey) => void;
  isAllSelectedInProject: (project: ProjectWithTasks) => boolean;
  isSomeSelectedInProject: (project: ProjectWithTasks) => boolean;
  toggleAllInProject: (project: ProjectWithTasks) => void;
  selectedIds: Set<number>;
  toggleTask: (taskId: number) => void;
  handleUnarchive: (task: TaskWithStats) => void;
  openMergeModal: (task: TaskWithStats) => void;
  openEditModal: (task: TaskWithStats) => void;
  openDeleteConfirm: (task: TaskWithStats) => void;
}

function ProjectTasksTable({
  project,
  currentUserId,
  isAdmin,
  projectManagerById,
  stateFilter,
  onToggleState,
  onClearFilter,
  sortKey,
  sortDir,
  handleSort,
  isAllSelectedInProject,
  isSomeSelectedInProject,
  toggleAllInProject,
  selectedIds,
  toggleTask,
  handleUnarchive,
  openMergeModal,
  openEditModal,
  openDeleteConfirm,
}: ProjectTasksTableProps) {
  const { data: states, isLoading: statesLoading } = useProjectStates(project.id);

  // D-22: editable iff admin OR currentUser is this project's PM.
  const pmId = projectManagerById.get(project.id) ?? null;
  const canEdit = isAdmin || (currentUserId != null && pmId === currentUserId);

  // D-08, D-11 filter: empty Set = show all.
  // Resolves task -> stateId via FK first, then Phase 17 D-12 dual-write name-fallback
  // (matches TaskStateChip.tsx — getManagerTasks doesn't return taskStateId).
  const stateByName = new Map<string, number>(
    (states ?? []).map((s) => [s.name, s.id]),
  );
  const visibleTasks =
    stateFilter.size === 0
      ? project.tasks
      : project.tasks.filter((t) => {
          if (t.taskStateId != null) return stateFilter.has(t.taskStateId);
          if (t.status) {
            const fallbackId = stateByName.get(t.status);
            return fallbackId != null && stateFilter.has(fallbackId);
          }
          return false;
        });

  const sortedTasks = sortKey ? sortTasks(visibleTasks, sortKey, sortDir) : visibleTasks;

  return (
    <div className="border-t border-gray-200">
      {/* Per-project filter chip-row (D-08, D-11) */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-gray-500 mr-1">Filter by state:</span>
        {statesLoading ? (
          <span className="text-xs text-gray-400">Loading…</span>
        ) : !states || states.length === 0 ? (
          <span className="text-xs text-gray-400">No states defined</span>
        ) : (
          <>
            {states.map((state: TaskState) => {
              const selected = stateFilter.has(state.id);
              return (
                <button
                  key={state.id}
                  type="button"
                  onClick={() => onToggleState(state.id)}
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${
                    selected ? 'ring-2 ring-primary-500 ring-offset-1' : 'opacity-50 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: state.color, color: chipTextColor(state.color) }}
                  aria-pressed={selected}
                >
                  {state.name}
                </button>
              );
            })}
            {stateFilter.size > 0 && (
              <button
                type="button"
                onClick={onClearFilter}
                className="ml-auto text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear
              </button>
            )}
          </>
        )}
      </div>

      {/* Tasks table */}
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={isAllSelectedInProject(project)}
                ref={(input) => {
                  if (input) input.indeterminate = isSomeSelectedInProject(project);
                }}
                onChange={() => toggleAllInProject(project)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>
            <SortHeader
              label="Task"
              sortKey="name"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
            />
            <SortHeader
              label="Code"
              sortKey="code"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
            />
            {/* Status column header — NOT a SortHeader (D-09 removed Status SortKey) */}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <SortHeader
              label="Origin"
              sortKey="origin"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
            />
            <SortHeader
              label="Entries"
              sortKey="entryCount"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
              align="right"
            />
            <SortHeader
              label="Hours"
              sortKey="totalHours"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
              align="right"
            />
            <SortHeader
              label="Planned"
              sortKey="plannedHours"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
              align="right"
            />
            <SortHeader
              label="Budget"
              sortKey="budget"
              currentKey={sortKey}
              currentDir={sortDir}
              onSort={handleSort}
              align="right"
            />
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedTasks.map((task) => {
            const isArchived = task.status === 'archived';
            const isEditable = task.origin === 'MS' || task.origin === null;
            return (
              <tr
                key={task.id}
                className={`hover:bg-gray-50 ${selectedIds.has(task.id) ? 'bg-blue-50' : ''}`}
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(task.id)}
                    onChange={() => toggleTask(task.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="px-6 py-4">
                  <div
                    className={`text-sm font-medium ${isArchived ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                  >
                    {task.name}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`text-sm font-mono ${isArchived ? 'text-gray-300' : 'text-gray-500'}`}
                  >
                    {task.code || '-'}
                  </span>
                </td>
                {/* Status chip (D-14, D-15, D-16, D-17 — TaskStateChip owns it) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {statesLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <TaskStateChip
                      task={task}
                      projectId={project.id}
                      states={states ?? []}
                      editable={canEdit}
                    />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {task.origin ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                      {task.origin}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-right text-sm ${isArchived ? 'text-gray-300' : 'text-gray-500'}`}
                >
                  {task.entryCount}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-right text-sm font-medium ${isArchived ? 'text-gray-400' : 'text-gray-900'}`}
                >
                  {formatHours(task.totalHours)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {task.plannedHours != null ? (
                    (() => {
                      const pct =
                        task.plannedHours > 0 ? (task.totalHours / task.plannedHours) * 100 : 0;
                      const colorClass =
                        pct > 100
                          ? 'text-red-600'
                          : pct >= 80
                            ? 'text-yellow-600'
                            : 'text-green-600';
                      return (
                        <span className={colorClass}>
                          {formatHours(task.totalHours)} / {formatHours(task.plannedHours)}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {task.budget != null ? (
                    (() => {
                      const spent = task.budgetSpent ?? 0;
                      const pct = task.budget > 0 ? (spent / task.budget) * 100 : 0;
                      const colorClass =
                        pct > 100
                          ? 'text-red-600'
                          : pct >= 80
                            ? 'text-yellow-600'
                            : 'text-green-600';
                      return (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={colorClass}>
                            {Math.round(spent).toLocaleString()} /{' '}
                            {Math.round(task.budget).toLocaleString()} CZK
                          </span>
                          {task.hardLimit && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700"
                              title="Hard limit — blocks entries when exceeded"
                            >
                              Hard
                            </span>
                          )}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {isEditable ? (
                    <div className="flex items-center justify-end gap-2">
                      {isArchived && (
                        <button
                          onClick={() => handleUnarchive(task)}
                          className="text-gray-400 hover:text-green-600"
                          title="Unarchive task"
                        >
                          <ArrowUturnLeftIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => openMergeModal(task)}
                        className="text-gray-400 hover:text-purple-600"
                        title="Merge with another task"
                      >
                        <ArrowsPointingInIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(task)}
                        className="text-gray-400 hover:text-blue-600"
                        title="Edit task"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(task)}
                        className="text-gray-400 hover:text-red-600"
                        title="Delete task"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
